import Reveal from "./Reveal";
import { PLACEHOLDER_IMAGE, placeholder } from "../../lib/constants";

export default function About({ content, uiStrings, elementVisibility, sectionId }) {
  const ABOUT_IMG = content?.image || PLACEHOLDER_IMAGE;
  const imgAlt = content?.img_alt || uiStrings?.general?.about_img_alt || "";

  const since = content?.since ? parseInt(content.since) : null;
  const currentYear = new Date().getFullYear();
  const yearsActive = since ? currentYear - since : null;

  const rawStats = typeof content?.stats === "string" ? JSON.parse(content.stats || "[]") : content?.stats;
  const stats = (rawStats || []).length > 0 ? (rawStats || []).map((stat, idx) => {
    if (idx === 0 && yearsActive) {
      return { ...stat, n: String(yearsActive) };
    }
    return stat;
  }) : [];

  const rawParagraphs = typeof content?.paragraphs === "string" ? JSON.parse(content.paragraphs || "[]") : content?.paragraphs;
  const paragraphs = (rawParagraphs?.length > 0
    ? rawParagraphs
    : [content?.description_1, content?.description_2].filter(Boolean)
  );
  const displayParagraphs = paragraphs.length > 0 ? paragraphs : [placeholder("long")];

  return (
    <section data-id="site-about" id={sectionId} className="bg-fendi/30 py-28 md:py-40">
      <div data-id="about-grid" className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-center">
        {elementVisibility?.about_image !== false && (
          <Reveal className="lg:col-span-5">
            <div data-id="about-image-wrapper" className="relative">
              <img
                src={ABOUT_IMG}
                alt={imgAlt}
                data-id="about-image"
                className="w-full h-[400px] md:h-[640px] object-cover"
                loading="lazy"
              />
              {stats.length >= 2 && (
                <div data-id="about-stats-badge" className="absolute -bottom-6 -right-6 bg-offwhite px-8 py-6 border border-fendi hidden md:block">
                  <p className="eyebrow text-warmgray" data-id="about-stats-badge-label">{stats[0].label}</p>
                  <p className="font-serif text-3xl text-softblack mt-2" data-id="about-stats-badge-value">{stats[1].prefix || ""}{stats[1].n} {stats[1].label?.toLowerCase()}</p>
                </div>
              )}
            </div>
          </Reveal>
        )}

        <Reveal delay={200} className={`${elementVisibility?.about_image !== false ? "lg:col-span-6 lg:col-start-7" : "lg:col-span-8 lg:col-start-3"}`}>
          <p className="eyebrow text-warmgray" data-id="about-eyebrow">{content?.eyebrow || uiStrings?.general?.about_eyebrow || placeholder("short")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]" data-id="about-headline">
            {content?.headline || uiStrings?.general?.about_headline || placeholder("medium")}
          </h2>
          {displayParagraphs.map((p, i) => (
            <p key={i} className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed mt-4 md:mt-8 font-light" data-id={`about-paragraph-${i}`}>
              {p}
            </p>
          ))}

          {elementVisibility?.about_stats !== false && (
            <div data-id="about-stats-grid" className="grid grid-cols-3 gap-4 md:gap-8 mt-10 md:mt-12 pt-10 md:pt-12 border-t border-fendi">
              {stats.map((stat, idx) => (
                <Stat key={idx} n={stat.prefix ? `${stat.prefix}${stat.n}` : stat.n} label={stat.label} />
              ))}
            </div>
          )}

          {content?.partner_logos?.filter(Boolean).length > 0 && (
            <div data-id="about-partners" className="mt-16 md:mt-20 pt-14 md:pt-16 border-t border-fendi/50">
              <div className="text-center mb-10 md:mb-12">
                <p className="eyebrow text-warmgray/70 tracking-[0.3em] text-[10px] md:text-[11px]" data-id="about-partners-eyebrow">
                  {uiStrings?.general?.about_partners || "ASSOCIAÇÕES"}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-fendi/30 rounded-lg overflow-hidden">
                {content.partner_logos.filter(Boolean).map((url, i) => (
                  <div key={i} data-id={`about-partner-logo-${i}`} className="flex items-center justify-center bg-fendi/30 h-24 md:h-28 p-5 md:p-7 group transition-colors duration-300 hover:bg-fendi/40">
                    <img src={url} alt={`Parceiro ${i + 1}`} className="max-h-full w-auto object-contain transition-all duration-300 group-hover:scale-105" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  const id = label?.toLowerCase().replace(/\s+/g, "-") || "unknown";
  return (
    <div data-id={`about-stat-${id}`}>
      <p data-id={`about-stat-n-${id}`} className="font-serif text-2xl sm:text-3xl md:text-5xl text-softblack break-words">{n}</p>
      <p data-id={`about-stat-label-${id}`} className="eyebrow text-warmgray mt-1 md:mt-2 text-[9px] sm:text-[10px] md:text-[11px]">{label}</p>
    </div>
  );
}
